import type { Project, Task, WorkflowState } from "@/lib/contract";

import { byPosition } from "./ordering";
import type { TableRows, Tables } from "./types";

/**
 * The columns of a project's board.
 *
 * A project uses its own columns if it has defined any, and the workspace's
 * shared columns otherwise. That is the rule the server applies too, so a new
 * project gets a usable board with no setup.
 */
export function columnsFor(
  states: TableRows<"workflowStates">,
  projectId: string,
): WorkflowState[] {
  const all = Object.values(states);
  const own = all.filter((state) => state.projectId === projectId);
  const columns = own.length > 0 ? own : all.filter((state) => state.projectId === null);
  return columns.sort(byPosition);
}

/** Where a new card lands when no column is chosen: the first backlog or todo. */
export function defaultColumn(
  states: TableRows<"workflowStates">,
  projectId: string,
): WorkflowState | undefined {
  const columns = columnsFor(states, projectId);
  return (
    columns.find((state) => state.type === "backlog" || state.type === "unstarted") ?? columns[0]
  );
}

/** Live cards in one column, in board order. */
export function tasksInColumn(tables: Tables, projectId: string, stateId: string): Task[] {
  return Object.values(tables.tasks)
    .filter((task) => task.projectId === projectId && task.stateId === stateId)
    .sort(byPosition);
}

/** ENG-42. */
export function displayKey(
  task: Pick<Task, "number">,
  project: Pick<Project, "key"> | undefined,
): string {
  return project ? `${project.key}-${task.number}` : `#${task.number}`;
}

/** The task a display key points at, e.g. from the ?task=ENG-42 search param. */
export function taskByKey(tables: Tables, key: string): Task | undefined {
  const match = /^([A-Z][A-Z0-9]*)-(\d+)$/.exec(key);
  if (!match) return undefined;
  const [, projectKey, number] = match;
  const project = Object.values(tables.projects).find((p) => p.key === projectKey);
  if (!project) return undefined;
  const wanted = Number(number);
  return Object.values(tables.tasks).find(
    (task) => task.projectId === project.id && task.number === wanted,
  );
}

/** Projects in sidebar order. */
export function sortedProjects(tables: Tables): Project[] {
  return Object.values(tables.projects).sort(byPosition);
}
